import React, { Fragment } from 'react';
import dayjs from 'dayjs';
import { Col, Form, FormGroup, Row } from 'react-bootstrap';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { monthList, OptionDateItem } from '../bf-hooks';

export interface DropdownDateFieldProps {
  nameField: string;
  labelField: string;
  valueField: string;
  endDate: string;
  isRequired: boolean;
  formatDate?: string;
}

const renderDayOptions = ( currentDate: string, isFirstLoad: boolean = false ) => {
  const days: OptionDateItem[] = [];
  const countDay = isFirstLoad ? 31 : dayjs( currentDate, 'YYYY-M' ).daysInMonth();

  for ( let i = 1; i <= countDay; i += 1 ) {
    const dayItem: OptionDateItem = {
      value: i,
      label: String( i ),
    };

    days.push( dayItem );
  }

  return days;
};

export const DropdownDateField: React.FC<DropdownDateFieldProps> = ( {
  nameField,
  labelField,
  valueField,
  endDate,
  isRequired,
  formatDate = 'YYYY-MM-DD',
} ) => {
  const { t } = useTranslation( [ 'widgets', 'base' ] );
  const { errors, control, setValue } = useFormContext();
  const monthOptions = monthList();
  const defaultDate = dayjs( valueField ? valueField : endDate, formatDate );

  const [ dataFull, setDataFull ] = React.useState<string>( valueField ? valueField : endDate );
  const [ dayField, setDayField ] = React.useState<string>( valueField ? defaultDate.format( 'D' ) : '' );
  const [ monthField, setMonthField ] = React.useState<string>( valueField ? defaultDate.format( 'M' ) : '' );
  const [ yearField, setYearField ] = React.useState<string>( valueField ? defaultDate.format( 'YYYY' ) : '' );

  let isfirstLoad = true;
  if ( valueField ) {
    isfirstLoad = false;
  }

  const defaultDayList = renderDayOptions( valueField ? valueField : endDate, isfirstLoad );
  const [ dayList, setDayList ] = React.useState<OptionDateItem[]>( defaultDayList );

  const yearOptions = React.useMemo<OptionDateItem[]>( () => {
    const years: OptionDateItem[] = [];

    const maxYear = dayjs( endDate ).year();
    const minYear = maxYear - 80;

    for ( let i = minYear; i <= maxYear; i += 1 ) {
      const yearItem: OptionDateItem = {
        value: i,
        label: String( i ),
      };

      years.push( yearItem );
    }

    return years;
  }, [ endDate ] );

  const handleChangeDay = ( e: React.ChangeEvent<HTMLSelectElement> ) => {
    const day = e.target.value;
    if ( !day ) {
      setValue( nameField, '', { shouldValidate: true } );
      clearDateFields();
      return;
    }

    const valueDate: string = dayjs( dataFull ).set( 'date', Number( day ) ).format( formatDate );
    setDayField( day );
    setDataFull( valueDate );

    setValue( nameField, valueDate, { shouldValidate: true } );
  };

  const handleChangeMonth = ( e: React.ChangeEvent<HTMLSelectElement> ) => {
    const month = e.target.value;
    if ( !month ) {
      setValue( nameField, '', { shouldValidate: true } );
      clearDateFields();
      return;
    }

    const valueDate: string = dayjs( dataFull ).set( 'month', Number( month ) - 1 ).format( formatDate );
    const days = renderDayOptions( valueDate );

    setDayList( days );
    setMonthField( month );
    setDataFull( valueDate );

    setValue( nameField, valueDate, { shouldValidate: true } );
  };

  const handleChangeYear = ( e: React.ChangeEvent<HTMLSelectElement> ) => {
    const year = e.target.value;
    if ( !year ) {
      setValue( nameField, '', { shouldValidate: true } );
      clearDateFields();
      return;
    }

    const valueDate: string = dayjs( dataFull ).set( 'year', Number( year ) ).format( formatDate );
    const days = renderDayOptions( valueDate );

    setDayList( days );
    setYearField( year );

    setDataFull( valueDate );
    setValue( nameField, valueDate, { shouldValidate: true } );
  };

  const clearDateFields = React.useCallback( () => {
    setDayField( '-1' );
    setMonthField( '-1' );
    setYearField( '-1' );
  }, [] );

  return (
    <Fragment>
      <Controller
        name={ nameField }
        control={ control }
        rules={ { required: isRequired } }
        defaultValue={ valueField }
        render={ ( props ) => (
          <FormGroup className="date-picker-field" controlId={ props.name }>
            <Form.Label id={ `${props.name}-label` }>
              { labelField }
            </Form.Label>
            <Row className="date-container">
              <Col md={ 6 } className="mb-3">
                <Form.Control
                  as="select"
                  className="form-select form-select-day"
                  defaultValue={ dayField }
                  onChange={ ( e: React.ChangeEvent<HTMLSelectElement> ) => handleChangeDay( e ) }
                  placeholder={ t( 'bookingFunnel.tariff.selectDay' ) }
                  isInvalid={ errors[props.name] !== undefined }
                >
                  <option value="">{ t( 'bookingFunnel.tariff.selectDay' ) }</option>
                  { dayList && dayList.map( ( el ) =>
                    <option
                      key={ el.value }
                      value={ el.value }
                    >
                      { el.label }
                    </option> ) }
                </Form.Control>
              </Col>
              <Col md={ 6 } className="mb-3">
                <Form.Control
                  as="select"
                  className="form-select form-select-month"
                  defaultValue={ monthField }
                  onChange={ ( e: React.ChangeEvent<HTMLSelectElement> ) => handleChangeMonth( e ) }
                  placeholder={ t( 'bookingFunnel.tariff.selectMonth' ) }
                  isInvalid={ errors[props.name] !== undefined }
                >
                  <option value="">{ t( 'bookingFunnel.tariff.selectMonth' ) }</option>
                  { monthOptions.map( ( el ) =>
                    <option
                      key={ el.value }
                      value={ el.value }
                    >
                      { el.label }
                    </option> ) }
                </Form.Control>
              </Col>
              <Col md={ 12 } className="mb-3">
                <Form.Control
                  as="select"
                  className="form-select form-select-year"
                  defaultValue={ yearField }
                  placeholder={ t( 'bookingFunnel.tariff.selectYear' ) }
                  onChange={ ( e: React.ChangeEvent<HTMLSelectElement> ) => handleChangeYear( e ) }
                  isInvalid={ errors[props.name] !== undefined }
                >
                  <option value="">{ t( 'bookingFunnel.tariff.selectYear' ) }</option>
                  { yearOptions.sort( ( a, b ) => b.value - a.value ).map( ( el ) =>
                    <option
                      key={ el.value }
                      value={ el.value }
                    >
                      { el.label }
                    </option> ) }
                </Form.Control>
              </Col>
            </Row>
            <Form.Control
              { ...props }
              type="hidden"
              isInvalid={ errors[props.name] !== undefined }
            />
            <Form.Control.Feedback type="invalid">
              { t( 'base:forms.messages.fieldRequired',
                { fieldLabel: labelField } ) }
            </Form.Control.Feedback>
          </FormGroup>
        ) }
      />
    </Fragment>
  );
};
